/**
 * Pure helpers for the ribbon rack (docs/Decisions.md D42). Mirrors the
 * server rules in src/shared/utils/ribbons.js closely enough to give live
 * feedback in the designer; the server stays the authority.
 *
 * Colour handling: a stripe is either a dcon token name, drawn as
 * `var(--<token>)` so it follows the theme, or an organization-chosen
 * `#RRGGBB` content colour, drawn as-is from data in an SVG `fill`.
 */

export const STRIPE_TOKENS = [
  'logo-blue',
  'deep',
  'accent',
  'lime',
  'logo-green',
  'medal-gold',
  'medal-silver',
  'medal-bronze',
  'medal-platinum',
];

export const MEDAL_TOKENS = ['medal-gold', 'medal-silver', 'medal-bronze', 'medal-platinum'];

export const TOKEN_LABELS = {
  'logo-blue': 'Logo blue',
  deep: 'Deep blue',
  accent: 'Orange',
  lime: 'Lime',
  'logo-green': 'Green',
  'medal-gold': 'Gold',
  'medal-silver': 'Silver',
  'medal-bronze': 'Bronze',
  'medal-platinum': 'Platinum',
};

export const DEVICE_TYPES = ['none', 'star', 'oak-leaf', 'numeral'];

export const DEVICE_LABELS = {
  none: 'No device',
  star: 'Star',
  'oak-leaf': 'Oak leaf',
  numeral: 'Level numeral',
};

export const METRIC_LABELS = {
  forum_posts: 'Forum threads started',
  forum_replies: 'Forum replies',
  investment_groups_joined: 'Investment groups joined',
  events_joined: 'Events attended',
  videos_watched: 'Videos watched',
  friends_invited: 'Friends who accepted an invitation',
  investments_made: 'Investments made',
};

export const LIMITS = { maxStripes: 12, maxLevels: 10, maxStripeWidth: 10, maxName: 60 };

const HEX = /^#[0-9A-Fa-f]{6}$/;

/** Fallback for a colour that is neither a token nor a valid hex. */
const FALLBACK_FILL = 'var(--line-strong)';

export function isHexColor(value) {
  return typeof value === 'string' && HEX.test(value);
}

export function isToken(value) {
  return STRIPE_TOKENS.includes(value);
}

/** The SVG fill for a stripe or device colour. */
export function colorFill(color) {
  if (isToken(color)) return `var(--${color})`;
  if (isHexColor(color)) return color;
  return FALLBACK_FILL;
}

/**
 * Lay stripes out across a ribbon of `width` units: each stripe's share is
 * its width weight over the total. Returns [{ x, width, fill }], vertical
 * bands left to right, covering the full width with no gap.
 */
export function stripeRects(stripes, width = 44) {
  const list = Array.isArray(stripes) && stripes.length ? stripes : [{ color: null, width: 1 }];
  const weights = list.map(s => (Number.isFinite(s.width) && s.width > 0 ? s.width : 1));
  const total = weights.reduce((a, b) => a + b, 0);
  let x = 0;
  return list.map((stripe, index) => {
    const w = index === list.length - 1 ? width - x : (weights[index] / total) * width;
    const rect = { x, width: w, fill: colorFill(stripe.color) };
    x += w;
    return rect;
  });
}

/** Accessible name for a rack item: "Deal Desk, Gold level". */
export function ribbonLabel(item) {
  if (!item) return '';
  const level = item.level && item.level.name ? `, ${item.level.name} level` : '';
  return `${item.name}${level}`;
}

/** Where an award is visible, in words. */
export function scopeLabel(visibility) {
  const scope = (visibility && visibility.scope) || 'platform';
  if (scope === 'organization') return 'Visible to members of the awarding organization';
  if (scope === 'group') {
    return visibility.groupName
      ? `Visible to the group ${visibility.groupName}`
      : 'Visible to one group';
  }
  return 'Visible platform wide';
}

/** Tooltip text: name, level, description and (for the owner) scope. */
export function ribbonTooltip(item, { showScope = false } = {}) {
  const parts = [ribbonLabel(item)];
  if (item.description) parts.push(item.description);
  if (showScope) parts.push(scopeLabel(item.visibility));
  if (item.hidden) parts.push('Hidden from others here');
  return parts.join('. ');
}

/** Split a list into rows of `perRow` (the top row holds the remainder, as on a uniform rack). */
export function rackRows(items, perRow = 4) {
  const n = Math.max(1, Math.min(12, Math.floor(perRow) || 4));
  const list = Array.isArray(items) ? items : [];
  if (!list.length) return [];
  const rows = [];
  const first = list.length % n || n;
  rows.push(list.slice(0, first));
  for (let i = first; i < list.length; i += n) rows.push(list.slice(i, i + n));
  return rows;
}

/** Move the item at `index` by `delta` (-1 up, +1 down); returns a new array. */
export function moveItem(list, index, delta) {
  const target = index + delta;
  if (index < 0 || index >= list.length || target < 0 || target >= list.length) return list;
  const next = [...list];
  const [item] = next.splice(index, 1);
  next.splice(target, 0, item);
  return next;
}

/** The PUT /member-badges/rack/me body for an edited list of rack items. */
export function arrangementFrom(items) {
  return {
    order: items.map(i => i.awardId),
    hidden: items.filter(i => i.hidden).map(i => i.awardId),
  };
}

/** Did the API refuse because the org needs the premium plan? */
export function isUpgradeRequired(error) {
  return Boolean(
    error && error.statusCode === 403 && error.details && error.details.upgradeRequired
  );
}

/** A fresh designer draft. */
export function emptyDraft() {
  return {
    kind: 'ribbon',
    name: '',
    description: '',
    design: {
      stripes: [
        { color: 'deep', width: 2 },
        { color: 'medal-gold', width: 1 },
        { color: 'deep', width: 2 },
      ],
      device: 'none',
    },
    levels: [],
    criteria: { type: 'manual' },
    visibility: { scope: 'platform' },
  };
}

/** A definition from the API, as a designer draft. */
export function draftFromDefinition(def) {
  return {
    kind: def.kind || 'ribbon',
    name: def.name || '',
    description: def.description || '',
    design: {
      stripes: (def.design && def.design.stripes ? def.design.stripes : []).map(s => ({ ...s })),
      device: (def.design && def.design.device) || 'none',
    },
    levels: (def.levels || []).map(l => ({ ...l })),
    criteria: { ...(def.criteria || { type: 'manual' }) },
    visibility: { ...(def.visibility || { scope: 'platform' }) },
  };
}

/**
 * Client-side check of a draft; returns a list of messages (empty when the
 * draft is fine). The server repeats every one of these.
 */
export function validateDraft(draft) {
  const errors = [];
  const name = (draft.name || '').trim();
  if (!name) errors.push('Give the ribbon a name.');
  else if (name.length > LIMITS.maxName)
    errors.push(`Keep the name to ${LIMITS.maxName} characters.`);

  const stripes = (draft.design && draft.design.stripes) || [];
  if (!stripes.length) errors.push('Add at least one stripe.');
  if (stripes.length > LIMITS.maxStripes) errors.push(`Use at most ${LIMITS.maxStripes} stripes.`);
  stripes.forEach((s, i) => {
    if (!isToken(s.color) && !isHexColor(s.color)) {
      errors.push(`Stripe ${i + 1}: enter a colour as #RRGGBB or pick a palette colour.`);
    }
    if (!Number.isInteger(s.width) || s.width < 1 || s.width > LIMITS.maxStripeWidth) {
      errors.push(
        `Stripe ${i + 1}: width must be a whole number from 1 to ${LIMITS.maxStripeWidth}.`
      );
    }
  });

  const levels = draft.levels || [];
  if (levels.length > LIMITS.maxLevels) errors.push(`Use at most ${LIMITS.maxLevels} levels.`);
  levels.forEach((l, i) => {
    if (!(l.name || '').trim()) errors.push(`Level ${i + 1}: give it a name.`);
  });

  const metric = draft.criteria && draft.criteria.type === 'metric';
  if (metric) {
    if (!draft.criteria.metric) errors.push('Pick the activity this ribbon counts.');
    if (levels.length === 0) {
      if (!Number.isInteger(draft.criteria.threshold) || draft.criteria.threshold < 1) {
        errors.push('Set how many times the activity earns the ribbon.');
      }
    } else {
      levels.forEach((l, i) => {
        if (!Number.isInteger(l.threshold) || l.threshold < 1) {
          errors.push(`Level ${i + 1}: set its threshold.`);
        } else if (
          i > 0 &&
          Number.isInteger(levels[i - 1].threshold) &&
          l.threshold <= levels[i - 1].threshold
        ) {
          errors.push('Each level needs a higher threshold than the one before it.');
        }
      });
    }
  }

  if (draft.visibility && draft.visibility.scope === 'group' && !draft.visibility.groupId) {
    errors.push('Pick the group that may see this ribbon.');
  }
  return [...new Set(errors)];
}

/** The API body for a draft: drops fields that do not apply to its criteria. */
export function definitionBody(draft) {
  const metric = draft.criteria && draft.criteria.type === 'metric';
  const levels = (draft.levels || []).map(l => {
    const out = { name: l.name.trim(), deviceColor: l.deviceColor || 'medal-gold' };
    if (metric) out.threshold = l.threshold;
    return out;
  });
  const criteria = metric
    ? {
        type: 'metric',
        metric: draft.criteria.metric,
        ...(levels.length ? {} : { threshold: draft.criteria.threshold }),
      }
    : { type: 'manual' };
  const visibility =
    draft.visibility.scope === 'group'
      ? { scope: 'group', groupId: draft.visibility.groupId }
      : { scope: draft.visibility.scope };
  return {
    kind: draft.kind,
    name: draft.name.trim(),
    description: (draft.description || '').trim(),
    design: {
      stripes: draft.design.stripes.map(s => ({ color: s.color, width: s.width })),
      device: draft.design.device,
    },
    levels,
    criteria,
    visibility,
  };
}

/** A rack item for the designer's live preview. */
export function previewItem(draft, levelIndex = 0) {
  const levels = draft.levels || [];
  const level = levels.length
    ? {
        index: Math.min(levelIndex, levels.length - 1),
        name: levels[Math.min(levelIndex, levels.length - 1)].name || `Level ${levelIndex + 1}`,
        deviceColor: levels[Math.min(levelIndex, levels.length - 1)].deviceColor || 'medal-gold',
        of: levels.length,
      }
    : null;
  const device = draft.design.device || 'none';
  return {
    awardId: 'preview',
    key: 'preview',
    kind: draft.kind,
    name: draft.name || 'Untitled ribbon',
    description: draft.description,
    design: {
      stripes: draft.design.stripes,
      device: {
        type: device,
        color: device === 'none' ? null : (level && level.deviceColor) || 'medal-gold',
      },
    },
    level,
    visibility: draft.visibility,
  };
}
